import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Send, RefreshCw, Copy, ExternalLink, Users2, X, Download, Clock } from "lucide-react";

const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];
const STATUS_STYLE = {
  unclaimed: "bg-amber-100 text-amber-800 border-amber-300",
  invite_sent: "bg-blue-100 text-blue-800 border-blue-300",
  claimed: "bg-emerald-100 text-emerald-800 border-emerald-300",
};
const STATUS_LABEL = { unclaimed: "Unclaimed", invite_sent: "Invite Sent", claimed: "Claimed" };

function Card({ label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 p-5 rounded-sm" data-testid={`summary-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="label-overline text-slate-500">{label}</div>
      <div className={`font-display text-4xl tracking-tighter mt-1 ${accent || "text-[#0A192F]"}`}>{value}</div>
    </div>
  );
}

export default function AdminUnclaimedPlayers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ graduation_year: "", province: "", invite_status: "", has_email: "", team: "", organization: "" });
  const [inviteFor, setInviteFor] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ""));
      const { data } = await api.get("/admin/unclaimed-players", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); setSelected(new Set()); /* eslint-disable-next-line */ }, [filters.graduation_year, filters.province, filters.invite_status, filters.has_email]);

  const gradYears = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => now - 2 + i);
  }, []);

  const openInvite = (row) => { setInviteFor(row); setInviteEmail(row.email || ""); };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) { toast.error("Enter an email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/players/${inviteFor.id}/invite`, { email: inviteEmail.trim() });
      const link = data.claim_link ? (data.claim_link.startsWith("http") ? data.claim_link : `${window.location.origin}${data.claim_link}`) : null;
      if (link && navigator.clipboard) { try { await navigator.clipboard.writeText(link); } catch (_) {} }
      toast.success(inviteFor.status === "invite_sent" ? "Invite resent — link copied" : "Invite sent — link copied");
      setInviteFor(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const copyLink = async (row) => {
    if (!row.claim_link) { toast.error("No active claim link — send an invite first"); return; }
    const link = row.claim_link.startsWith("http") ? row.claim_link : `${window.location.origin}${row.claim_link}`;
    try { await navigator.clipboard.writeText(link); toast.success("Claim link copied"); } catch (_) { toast.error("Copy failed"); }
  };

  const toggleOne = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allVisibleSelected = () => (data?.items || []).length > 0 && (data.items).every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(() => {
    const ids = (data?.items || []).map((r) => r.id);
    return allVisibleSelected() ? new Set() : new Set(ids);
  });

  const bulkInvite = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/players/bulk-invite", { player_ids: [...selected] });
      const parts = [`${data.invited} invited`];
      if (data.skipped_no_email) parts.push(`${data.skipped_no_email} skipped (no email)`);
      if (data.skipped_claimed) parts.push(`${data.skipped_claimed} already claimed`);
      if (data.skipped_conflict) parts.push(`${data.skipped_conflict} email in use`);
      toast.success(parts.join(" · "));
      setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Bulk invite failed"); }
    finally { setBulkBusy(false); }
  };

  const bulkMarkLater = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/players/bulk-mark-later", { player_ids: [...selected] });
      toast.success(`${data.updated} marked for later`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBulkBusy(false); }
  };

  const exportSelected = () => {
    const rows = (data?.items || []).filter((r) => selected.has(r.id));
    if (!rows.length) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = ["Name", "Team", "Organization", "Grad Year", "Province", "Created By", "Status", "Email"];
    const csv = [headers.join(","), ...rows.map((r) => [r.name, r.team_name, r.organization_name, r.graduation_year, r.province, r.created_by, r.status, r.email].map(esc).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "unclaimed-players.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const s = data?.summary || {};
  const m = data?.this_month || {};
  const items = data?.items || [];

  return (
    <div className="space-y-8" data-testid="unclaimed-players-page">
      <div>
        <div className="label-overline text-[#E63946] flex items-center gap-2"><UserPlus className="w-4 h-4" /> Growth Funnel</div>
        <h1 className="font-display text-4xl uppercase tracking-tighter mt-1">Unclaimed Players</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">Convert existing player records into active users. Every roster you build grows the national player database.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="Total Unclaimed" value={s.total_unclaimed ?? "—"} accent="text-amber-600" />
        <Card label="Invites Sent" value={s.invites_sent ?? "—"} accent="text-blue-600" />
        <Card label="Claimed Players" value={s.claimed_players ?? "—"} accent="text-emerald-600" />
        <Card label="Conversion" value={`${s.conversion_pct ?? 0}%`} accent="text-[#E63946]" />
      </div>

      <div className="bg-[#0A192F] text-white p-5 rounded-sm" data-testid="this-month-report">
        <div className="label-overline text-white/60">This Month</div>
        <div className="flex flex-wrap gap-x-10 gap-y-2 mt-2 text-sm">
          <span><span className="font-display text-2xl mr-2">{m.new_players ?? 0}</span>new players added</span>
          <span><span className="font-display text-2xl mr-2">{m.invited ?? 0}</span>invited</span>
          <span><span className="font-display text-2xl mr-2">{m.claimed ?? 0}</span>claimed</span>
          <span className="text-[#ff8a94]"><span className="font-display text-2xl mr-2">{m.conversion_pct ?? 0}%</span>conversion</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="label-overline text-[10px]">Status</label>
          <select data-testid="filter-status" value={filters.invite_status} onChange={(e) => setFilters({ ...filters, invite_status: e.target.value })} className="mt-1 block px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
            <option value="">All</option>
            <option value="unclaimed">Unclaimed</option>
            <option value="invite_sent">Invite Sent</option>
            <option value="claimed">Claimed</option>
          </select>
        </div>
        <div>
          <label className="label-overline text-[10px]">Grad Year</label>
          <select data-testid="filter-grad" value={filters.graduation_year} onChange={(e) => setFilters({ ...filters, graduation_year: e.target.value })} className="mt-1 block px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
            <option value="">All</option>
            {gradYears.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label className="label-overline text-[10px]">Province</label>
          <select data-testid="filter-province" value={filters.province} onChange={(e) => setFilters({ ...filters, province: e.target.value })} className="mt-1 block px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
            <option value="">All</option>
            {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="label-overline text-[10px]">Has Email</label>
          <select data-testid="filter-has-email" value={filters.has_email} onChange={(e) => setFilters({ ...filters, has_email: e.target.value })} className="mt-1 block px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
            <option value="">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {(filters.invite_status || filters.graduation_year || filters.province || filters.has_email) && (
          <button data-testid="clear-filters" onClick={() => setFilters({ graduation_year: "", province: "", invite_status: "", has_email: "", team: "", organization: "" })} className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 hover:text-[#E63946]">Clear</button>
        )}
      </div>

      {selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="flex flex-wrap items-center gap-3 bg-[#0A192F] text-white px-4 py-3 rounded-sm">
          <span className="text-sm font-semibold" data-testid="bulk-selected-count">{selected.size} selected</span>
          <div className="flex-1" />
          <button data-testid="bulk-invite-btn" onClick={bulkInvite} disabled={bulkBusy} className="inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#c92f3c] px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"><Send className="w-3.5 h-3.5" /> Send Invite to Selected</button>
          <button data-testid="bulk-export-btn" onClick={exportSelected} className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><Download className="w-3.5 h-3.5" /> Export Selected</button>
          <button data-testid="bulk-later-btn" onClick={bulkMarkLater} disabled={bulkBusy} className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"><Clock className="w-3.5 h-3.5" /> Mark for Later</button>
          <button data-testid="bulk-clear-btn" onClick={() => setSelected(new Set())} className="text-xs uppercase tracking-wider text-white/60 hover:text-white">Clear</button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="px-4 py-3 w-10"><input data-testid="select-all-checkbox" type="checkbox" checked={allVisibleSelected()} onChange={toggleAll} /></th>
              <th className="text-left px-4 py-3">Player</th>
              <th className="text-left px-4 py-3">Team</th>
              <th className="text-left px-4 py-3">Organization</th>
              <th className="text-left px-4 py-3">Grad</th>
              <th className="text-left px-4 py-3">Prov</th>
              <th className="text-left px-4 py-3">Created By</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-12 text-center">
                <Users2 className="w-10 h-10 mx-auto text-slate-300" />
                <p className="mt-3 font-semibold text-slate-700">No players match these filters</p>
                <p className="text-slate-500 text-xs mt-1">Add players to a team roster to start growing the database.</p>
              </td></tr>
            ) : items.map((row) => (
              <tr key={row.id} data-testid={`unclaimed-row-${row.id}`} className={`hover:bg-slate-50 ${selected.has(row.id) ? "bg-red-50/40" : ""}`}>
                <td className="px-4 py-3"><input data-testid={`select-${row.id}`} type="checkbox" checked={selected.has(row.id)} onChange={() => toggleOne(row.id)} /></td>
                <td className="px-4 py-3 font-semibold">{row.name}</td>
                <td className="px-4 py-3">{row.team_id ? <Link to={`/admin/teams/${row.team_id}`} className="text-[#0A192F] hover:text-[#E63946]">{row.team_name || "Team"}</Link> : <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3">{row.organization_name || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3">{row.graduation_year || "—"}</td>
                <td className="px-4 py-3">{row.province || "—"}</td>
                <td className="px-4 py-3 text-slate-500">{row.created_by}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider border px-2 py-0.5 rounded-sm ${STATUS_STYLE[row.status]}`}>{STATUS_LABEL[row.status]}</span>
                  {row.marked_for_later && <span data-testid={`later-${row.id}`} className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider border border-slate-300 bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm"><Clock className="w-3 h-3" /> Later</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {row.status !== "claimed" && (
                      <button data-testid={`invite-btn-${row.id}`} onClick={() => openInvite(row)} title={row.status === "invite_sent" ? "Resend invite" : "Send claim invite"} className="p-2 hover:bg-slate-100 rounded-sm text-[#0A192F]">
                        {row.status === "invite_sent" ? <RefreshCw className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                      </button>
                    )}
                    {row.claim_link && (
                      <button data-testid={`copy-btn-${row.id}`} onClick={() => copyLink(row)} title="Copy claim link" className="p-2 hover:bg-slate-100 rounded-sm text-slate-600"><Copy className="w-4 h-4" /></button>
                    )}
                    {row.slug && (
                      <Link data-testid={`profile-btn-${row.id}`} to={`/prospects/${row.slug}`} title="View profile" className="p-2 hover:bg-slate-100 rounded-sm text-slate-600"><ExternalLink className="w-4 h-4" /></Link>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {inviteFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="invite-modal">
          <div className="bg-white w-full max-w-md rounded-sm p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl uppercase tracking-tighter">{inviteFor.status === "invite_sent" ? "Resend Invite" : "Send Claim Invite"}</h3>
              <button onClick={() => setInviteFor(null)} className="p-2 hover:bg-slate-100 rounded-sm"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-600 mt-2">Invite <span className="font-semibold">{inviteFor.name}</span> to claim their profile. All stats, rankings, media and history stay attached — ownership simply transfers.</p>
            <div className="mt-4">
              <label className="label-overline text-[10px]">Email</label>
              <input data-testid="invite-modal-email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="player@example.com" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setInviteFor(null)} className="px-5 py-2 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm">Cancel</button>
              <button data-testid="invite-modal-send" onClick={sendInvite} disabled={busy} className="px-5 py-2 bg-[#0A192F] text-white text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">{busy ? "Sending…" : "Send Invite"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
